/**
 * Contracts for ordering local cart mutations against irreversible side-effects
 * (kitchen/bill print, one-click pay, checkout navigation, payment finalize).
 *
 * The surface is split into narrow roles (ISP) so every call site depends only on
 * the slice it uses, and consumers inject the abstract {@link CartInteractionCoordinator}
 * token (DIP) — never a concrete class — so the implementation can be swapped later
 * (e.g. a signals-based one in 2.14.0) without touching call sites.
 */

/** Producer side: register an in-flight cart mutation so gates can wait for it. */
export interface AddInteractionProducer {
  /**
   * Register an in-flight add. Returns the SAME work promise so call sites can chain on it.
   * The tracking entry self-removes when the work settles (success, error or cancel).
   */
  track<T>(work: Promise<T>): Promise<T>;
}

/** Consumer side: wait until cart mutations that started before this call have settled. */
export interface AddInteractionGate {
  /**
   * Resolve once every add that was in flight at call time has settled. Snapshotting at
   * call time gives happens-before semantics and avoids starvation under continuous tapping.
   */
  whenIdle(): Promise<void>;
}

/** Boundary owners only: drop tracked work and release pending gates. */
export interface AddInteractionLifecycle {
  /** Drop all tracked work and release any pending gate. Call at app pause/resume, screen exit, after pay. */
  reset(): void;
}

/**
 * Abstract DI token combining the role contracts. Inject THIS, not the concrete service.
 * A future signals-based implementation can replace the concrete one behind this token
 * without any change to producers/consumers (LSP + DIP).
 */
export abstract class CartInteractionCoordinator
  implements AddInteractionProducer, AddInteractionGate, AddInteractionLifecycle
{
  abstract track<T>(work: Promise<T>): Promise<T>;
  abstract whenIdle(): Promise<void>;
  abstract reset(): void;
}
